/**
 * Portefeuille de projets — filtre par équipe/statut/recherche, création via `ProjectForm`
 * dans un `Modal`.
 *
 * Grille en planche B (`@exidis/ui` 1.5.0, reference.md §11.7, décision du 2026-09-28) :
 * `CELLS_PLANCHE`/`CELL_PLANCHE` (alias documentés de `.cells`/`.cell`) — gouttière de 16 px
 * portée par le paquet, chaque carte fermée par son cadre 2 px et ses quatre équerres `.ticks`.
 * La même grammaire que toute grille de cellules du produit, y compris responsive. Remplace
 * la planche D collée (gap 0, traits partagés, équerres aux croisements) du 2026-09-26.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';
import { fetchAllPages } from '../lib/pagination.js';
import { useAsync } from '../hooks/useAsync.js';
import { appAlert } from '../dialogs/service.js';
import {
  Empty,
  LoadError,
  PageBody,
  PageHeader,
  PrimaryButton,
  Progress,
  StatusTag,
  TruncatedListWarning,
  taskReference,
} from '../components/common.jsx';
import { Modal } from '../components/Modal.jsx';
import { SeatNotice } from '../components/SeatNotice.jsx';
import { ProjectForm } from '../components/ProjectForm.jsx';
import { BTN_GHOST_SM, CELLS_PLANCHE, CELL_PLANCHE, INPUT_SM, SELECT_SM } from '../ui/theme.js';

const STATUS_OPTION_KEYS = [
  { value: '', key: 'projectStatus.all' },
  { value: 'draft', key: 'projectStatus.draft' },
  { value: 'active', key: 'projectStatus.active' },
  { value: 'on_hold', key: 'projectStatus.onHold' },
  { value: 'done', key: 'projectStatus.done' },
  { value: 'archived', key: 'projectStatus.archived' },
];

export function ProjectsPage() {
  const { t } = useTranslation();
  const STATUS_OPTIONS = STATUS_OPTION_KEYS.map((option) => ({
    value: option.value,
    label: t(option.key),
  }));
  const formatPeriod = (project) => {
    if (!project.startDate && !project.endDate) return t('projects.noPeriod');
    return `${project.startDate ?? '…'} → ${project.endDate ?? '…'}`;
  };
  const navigate = useNavigate();
  const [teamId, setTeamId] = useState('');
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const { data: teamsData } = useAsync((signal) => fetchAllPages('/teams', { signal }), []);
  const teams = teamsData?.data ?? [];

  const {
    data: projectsData,
    loading,
    error,
    reload,
  } = useAsync(
    (signal) => {
      const params = new URLSearchParams();
      if (teamId) params.set('teamId', teamId);
      if (status) params.set('status', status);
      if (q) params.set('q', q);
      const query = params.toString();
      return fetchAllPages(`/projects${query ? `?${query}` : ''}`, { signal });
    },
    [teamId, status, q],
  );
  const projects = projectsData?.data ?? [];

  const createProject = async (values) => {
    setSubmitting(true);
    try {
      await api.post('/projects', values);
      setCreating(false);
      reload();
    } catch (error) {
      // Refus courant et attendu : clé de projet déjà prise dans l'équipe (409). Sans ce
      // `catch`, le formulaire restait ouvert sans rien dire — le même symptôme que la
      // création d'équipe muette. La boîte de dialogue reste ouverte pour corriger la saisie.
      await appAlert(t('projects.createFailed', { message: error.message }), {
        title: t('projects.newProject'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow={t('projects.eyebrow')}
        title={t('projects.title')}
        subtitle={t('projects.subtitle')}
        primary={
          <PrimaryButton onClick={() => setCreating(true)}>
            {t('projects.newProject')}
          </PrimaryButton>
        }
      />

      {/* Mode `registre` seulement : explique l'absence des projets d'une organisation sans siège. */}
      <SeatNotice />

      {/* Même partage que `PageHeader` : le trait 2 px sur l'élément extérieur, non borné,
          et le contenu borné par `--largeur-corps` pour rester aligné sur le corps. */}
      <div className="border-b-2 border-line">
        <PageBody className="flex flex-wrap items-center gap-2 px-4 py-3">
          <select
            aria-label={t('projects.filterByTeam')}
            className={SELECT_SM}
            value={teamId}
            onChange={(event) => setTeamId(event.target.value)}
          >
            <option value="">{t('projects.allTeams')}</option>
            {teams.map((team) => (
              <option key={team.id} value={team.id}>
                {team.name}
              </option>
            ))}
          </select>

          <select
            aria-label={t('projects.filterByStatus')}
            className={SELECT_SM}
            value={status}
            onChange={(event) => setStatus(event.target.value)}
          >
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <input
            aria-label={t('projects.searchProject')}
            className={`${INPUT_SM} min-w-48`}
            placeholder={t('projects.searchPlaceholder')}
            value={q}
            onChange={(event) => setQ(event.target.value)}
          />
        </PageBody>
      </div>

      <PageBody className="p-4">
        {error && <LoadError error={error} onRetry={reload} className="mb-3" />}
        {(projectsData?.truncated || teamsData?.truncated) && (
          <div className="mb-3">
            <TruncatedListWarning />
          </div>
        )}

        {!loading && !error && projects.length === 0 ? (
          <Empty
            action={
              <button type="button" className={BTN_GHOST_SM} onClick={() => setCreating(true)}>
                {t('projects.createFirst')}
              </button>
            }
          >
            {t('projects.emptyFiltered')}
          </Empty>
        ) : (
          <div className={`${CELLS_PLANCHE} grid-cols-1 sm:grid-cols-2 lg:grid-cols-3`}>
            {projects.map((project) => (
              <button
                key={project.id}
                type="button"
                className={`${CELL_PLANCHE} flex flex-col gap-2 bg-paper p-4 text-left transition-colors hover:bg-line-soft`}
                onClick={() => navigate(`/projets/${project.id}`)}
              >
                <span className="ticks" aria-hidden="true" />
                <div className="flex items-center justify-between gap-2">
                  <span className="font-mono text-xs uppercase tracking-label text-ink-faint">
                    {project.key}
                  </span>
                  <StatusTag status={project.status} />
                </div>
                <p className="text-sm font-semibold text-ink">{project.name}</p>
                <p className="font-mono text-[11px] text-ink-faint">{formatPeriod(project)}</p>
                <Progress
                  value={project.progress ?? 0}
                  label={t('projects.progressLabel', { name: project.name })}
                />
                <p className="text-[11px] text-ink-faint">
                  {t('projects.taskCount', { count: project.taskCount ?? 0 })}
                  {project.taskCount
                    ? t('projects.lastTaskSuffix', {
                        ref: taskReference(project.key, project.taskCount),
                      })
                    : ''}
                </p>
              </button>
            ))}
          </div>
        )}
      </PageBody>

      {creating && (
        <Modal title={t('projects.modalTitle')} onClose={() => setCreating(false)}>
          <ProjectForm
            teams={teams}
            submitting={submitting}
            onSubmit={createProject}
            onCancel={() => setCreating(false)}
          />
        </Modal>
      )}
    </div>
  );
}
